import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * Product-tour offer state. The persistent "Want a product tour?" bar shows
 * while `dismissed` is false; "Got it, don't show again" permanently dismisses
 * it (customer_users.tour_dismissed_at). Separate from onboarding — a returning
 * customer keeps being offered the tour until they kill it.
 */
export function useTourDismissed() {
  return useQuery({
    queryKey: ['tour-dismissed'],
    queryFn: async (): Promise<boolean> => {
      const { data: authData } = await supabase.auth.getUser();
      const uid = authData.user?.id;
      if (!uid) return true; // not signed in → don't offer
      const { data, error } = await supabase
        .schema('orders')
        .from('customer_users')
        .select('tour_dismissed_at')
        .eq('id', uid)
        .maybeSingle();
      if (error || !data) return true;
      return data.tour_dismissed_at != null;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Permanently hide the tour offer for the caller. */
export function useDismissTour() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase.schema('orders').rpc('dismiss_tour');
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tour-dismissed'] }),
  });
}

/**
 * Which deep-dive section tutorials the caller has finished
 * (customer_users.tour_sections_done). Drives the /welcome tile state
 * (light-green "Completed" vs baby-blue "Not started") + the progress bar.
 */
export function useTourProgress() {
  return useQuery({
    queryKey: ['tour-progress'],
    queryFn: async (): Promise<string[]> => {
      const { data: authData } = await supabase.auth.getUser();
      const uid = authData.user?.id;
      if (!uid) return [];
      const { data, error } = await supabase
        .schema('orders')
        .from('customer_users')
        .select('tour_sections_done')
        .eq('id', uid)
        .maybeSingle();
      if (error || !data) return [];
      return (data.tour_sections_done as string[] | null) ?? [];
    },
    staleTime: 30 * 1000,
  });
}

/** Record one section tutorial as complete for the caller (idempotent). */
export function useMarkTourSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (key: string) => {
      const { error } = await supabase.schema('orders').rpc('mark_tour_section', { p_key: key });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tour-progress'] }),
  });
}
